import React from 'react'
import type { PairingStatus, ClueStatus } from '../types'

// Status configuration — single source of truth. Order here drives chip
// presentation order in filters / badges. Curatorial axis only; "used" is
// derived (see isPairingUsed) and quality is its own axis (rating 1–5).
// Rejected sits second so the "killed" signal is prominent and easy to find.
export const PAIRING_STATUS_CONFIG: Record<PairingStatus, { label: string; icon: string; color: string; borderColor: string; style?: React.CSSProperties }> = {
  'none': { label: 'No Status', icon: '○', color: 'bg-gray-100 text-gray-500', borderColor: 'border-gray-300' },
  // Rejected uses a custom inline SVG (see RejectedIcon below) — the Unicode
  // ⊘ rendered too small and inconsistent across fonts. Icon string kept as a
  // fallback for any non-StatusIcon consumer.
  'rejected': { label: 'Rejected', icon: '⊘', color: 'bg-red-100 text-red-600', borderColor: 'border-red-300' },
  'draft': { label: 'Draft', icon: '◐', color: 'bg-sky-100 text-sky-600', borderColor: 'border-sky-300' },
  'approved': { label: 'Approved', icon: '✓', color: 'bg-emerald-100 text-emerald-600', borderColor: 'border-emerald-300' },
}

export const CLUE_STATUS_CONFIG: Record<ClueStatus | 'empty', { label: string; icon: string; color: string; borderColor: string }> = {
  'final': { label: 'Final', icon: 'F', color: 'bg-indigo-100 text-indigo-600', borderColor: 'border-indigo-300' },
  'draft': { label: 'Draft', icon: 'D', color: 'bg-orange-100 text-orange-600', borderColor: 'border-orange-300' },
  'empty': { label: 'No Clue', icon: '–', color: 'bg-gray-100 text-gray-500', borderColor: 'border-gray-300' },
}

// Custom SVG: circle with a diagonal slash through the center. Sized via
// `1em` so it scales with the surrounding font-size; uses currentColor so
// the existing text-red-* classes tint it without extra props.
function RejectedIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="5" y1="12" x2="19" y2="12" transform="rotate(-45 12 12)" />
    </svg>
  )
}

// Simple icon component - use this everywhere for consistency.
// Falls back to the 'none' config if a stale / unknown status sneaks through —
// guards against persisted data with a status not in the current enum.
export function StatusIcon({ status, className = '' }: { status: PairingStatus; className?: string }) {
  if (status === 'rejected') {
    return <RejectedIcon className={className} />
  }
  const config = PAIRING_STATUS_CONFIG[status] ?? PAIRING_STATUS_CONFIG.none
  return (
    <span className={className} style={config.style}>
      {config.icon}
    </span>
  )
}

export function ClueIcon({ status, className = '' }: { status: ClueStatus | 'empty'; className?: string }) {
  const config = CLUE_STATUS_CONFIG[status] ?? CLUE_STATUS_CONFIG.empty
  return <span className={className}>{config.icon}</span>
}

// For backwards compatibility with existing code
const PAIRING_STATUSES = Object.entries(PAIRING_STATUS_CONFIG).map(([value, config]) => ({
  value: value as PairingStatus,
  label: config.label,
  short: config.icon,
  color: config.color,
  style: config.style,
}))

const CLUE_STATUSES = Object.entries(CLUE_STATUS_CONFIG).map(([value, config]) => ({
  value: value as ClueStatus | 'empty',
  label: config.label,
  short: config.icon,
  color: config.color,
}))

type StatusFilterProps = {
  selectedPairingStatuses: ReadonlyArray<PairingStatus>
  selectedClueStatuses: ReadonlyArray<ClueStatus | 'empty'>
  onPairingStatusChange: (statuses: PairingStatus[]) => void
  onClueStatusChange: (statuses: (ClueStatus | 'empty')[]) => void
  hiddenPairingStatuses?: PairingStatus[]
  showClueFilter?: boolean
  compact?: boolean
}

export function StatusFilter({
  selectedPairingStatuses,
  selectedClueStatuses,
  onPairingStatusChange,
  onClueStatusChange,
  hiddenPairingStatuses = [],
  showClueFilter = true,
  compact = false
}: StatusFilterProps) {
  const visiblePairingStatuses = hiddenPairingStatuses.length > 0
    ? PAIRING_STATUSES.filter(s => !hiddenPairingStatuses.includes(s.value))
    : PAIRING_STATUSES
  const togglePairingStatus = (status: PairingStatus) => {
    if (selectedPairingStatuses.includes(status)) {
      onPairingStatusChange(selectedPairingStatuses.filter(s => s !== status))
    } else {
      onPairingStatusChange([...selectedPairingStatuses, status])
    }
  }

  const toggleClueStatus = (status: ClueStatus | 'empty') => {
    if (selectedClueStatuses.includes(status)) {
      onClueStatusChange(selectedClueStatuses.filter(s => s !== status))
    } else {
      onClueStatusChange([...selectedClueStatuses, status])
    }
  }

  if (compact) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        {visiblePairingStatuses.length > 0 && (
          <div className="flex gap-1">
            {visiblePairingStatuses.map(status => (
              <button
                key={status.value}
                onClick={() => togglePairingStatus(status.value)}
                title={status.label}
                className={`w-7 h-7 text-xs rounded-md transition-all flex items-center justify-center ${selectedPairingStatuses.includes(status.value)
                  ? status.color
                  : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
              >
                <StatusIcon status={status.value} />
              </button>
            ))}
          </div>
        )}
        {showClueFilter && (
          <>
            {visiblePairingStatuses.length > 0 && <span className="text-gray-200">|</span>}
            <div className="flex gap-1">
              {CLUE_STATUSES.map(status => (
                <button
                  key={status.value}
                  onClick={() => toggleClueStatus(status.value)}
                  title={status.label}
                  className={`w-7 h-7 text-xs rounded-md transition-all flex items-center justify-center ${selectedClueStatuses.includes(status.value)
                    ? status.color
                    : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    }`}
                >
                  {status.short}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {visiblePairingStatuses.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Pairing Status
          </span>
          <div className="flex flex-wrap gap-1 mt-1">
            {visiblePairingStatuses.map(status => (
              <button
                key={status.value}
                onClick={() => togglePairingStatus(status.value)}
                className={`px-2 py-1 text-xs rounded transition-all ${selectedPairingStatuses.includes(status.value)
                  ? `${status.color} ring-2 ring-offset-1 ring-current`
                  : 'bg-white text-gray-400 border border-gray-200 hover:bg-gray-50 hover:text-gray-600'
                  }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showClueFilter && (
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Clue Status
          </span>
          <div className="flex flex-wrap gap-1 mt-1">
            {CLUE_STATUSES.map(status => (
              <button
                key={status.value}
                onClick={() => toggleClueStatus(status.value)}
                className={`px-2 py-1 text-xs rounded transition-all ${selectedClueStatuses.includes(status.value)
                  ? `${status.color} ring-2 ring-offset-1 ring-current`
                  : 'bg-white text-gray-400 border border-gray-200 hover:bg-gray-50 hover:text-gray-600'
                  }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Badge component for displaying status. Same fallback as StatusIcon.
export function StatusBadge({ status, compact = false, isTop = true }: { status: PairingStatus; compact?: boolean; isTop?: boolean }) {
  const config = PAIRING_STATUS_CONFIG[status] ?? PAIRING_STATUS_CONFIG.none

  if (compact) {
    const rounding = isTop ? 'rounded-t-sm' : 'rounded-b-sm'
    return (
      <span
        className={`w-5 h-5 text-[10px] border ${config.borderColor} flex items-center justify-center ${config.color} ${rounding}`}
        title={config.label}
      >
        <StatusIcon status={status} />
      </span>
    )
  }

  return (
    <span className={`px-2 py-0.5 text-xs rounded-md font-medium ${config.color}`}>
      {config.label}
    </span>
  )
}

export function ClueBadge({ status, compact = false, isBottom = true }: { status: ClueStatus | 'empty'; compact?: boolean; isBottom?: boolean }) {
  const config = CLUE_STATUS_CONFIG[status] ?? CLUE_STATUS_CONFIG.empty

  if (compact) {
    const rounding = isBottom ? 'rounded-b-sm' : 'rounded-t-sm'
    return (
      <span
        className={`w-5 h-5 text-[10px] border ${config.borderColor} flex items-center justify-center ${config.color} ${rounding} -mt-px`}
        title={config.label}
      >
        <ClueIcon status={status} />
      </span>
    )
  }

  return (
    <span className={`px-2 py-0.5 text-xs rounded-md font-medium ${config.color}`}>
      {config.label}
    </span>
  )
}

// Combined badge stack - always shows both pairing and clue status
// With optional status picker menu
export function BadgeStack({
  pairingStatus = 'none',
  clueStatus = 'empty',
  onPairingStatusChange
}: {
  pairingStatus?: PairingStatus | undefined
  clueStatus?: ClueStatus | 'empty' | undefined
  onPairingStatusChange?: ((status: PairingStatus) => void) | undefined
}) {
  const [showMenu, setShowMenu] = React.useState(false)

  return (
    <div className="flex flex-col w-5 shrink-0 relative">
      {onPairingStatusChange ? (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            setShowMenu(!showMenu)
          }}
          className="focus:outline-none hover:ring-1 hover:ring-gray-300 rounded-t-sm"
          title="Click to change status"
        >
          <StatusBadge status={pairingStatus} compact isTop={true} />
        </button>
      ) : (
        <StatusBadge status={pairingStatus} compact isTop={true} />
      )}
      <ClueBadge status={clueStatus} compact isBottom={true} />

      {/* Status picker menu */}
      {showMenu && onPairingStatusChange && (
        <>
          <div
            className="fixed inset-0 z-10"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              setShowMenu(false)
            }}
          />
          <div
            className="absolute left-6 top-0 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[100px]"
            onPointerDown={(e) => e.stopPropagation()}
          >
            {(['none', 'rejected', 'draft', 'approved'] as PairingStatus[]).map(status => {
              const config = PAIRING_STATUS_CONFIG[status]
              return (
                <button
                  key={status}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    onPairingStatusChange(status)
                    setShowMenu(false)
                  }}
                  className={`w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-gray-50 ${pairingStatus === status ? 'bg-gray-100' : ''}`}
                >
                  <span className={`w-4 h-4 rounded flex items-center justify-center text-[10px] ${config.color}`}>
                    <StatusIcon status={status} />
                  </span>
                  {config.label}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
